/* eslint-disable @typescript-eslint/brace-style */
import { useNavigate, useLocation } from 'react-router-dom';
import { UserProfile } from 'api/account/models/UserProfile';
import { Paged } from 'api/common/types/Page';
import InstallerAccessesService from 'api/installerAccesses/InstallerAccessesService';
import { InstallerAccessGrantDto, InstallerAccessGrantStatus } from 'api/installerAccesses/models/InstallerAccessGrantDto';
import { InstallerAccessGrantsSearchCriteria } from 'api/installerAccesses/models/InstallerAccessGrantsSearchCriteria';
import UsersService from 'api/users/UsersService';
import ListingTable, { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import Logger from 'common/services/Logger';
import Loading from 'common/services/Loading';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import styles from './InstallerAccessesScreen.module.scss';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import toast from 'react-hot-toast';
import { Badge, Breadcrumb, Button, Card, Container, Dropdown, Form, Stack } from 'react-bootstrap';

const computeStatus = (grant: InstallerAccessGrantDto): InstallerAccessGrantStatus => {
    const now = new Date();
    const start = new Date(grant.startDate);
    const end = new Date(grant.endDate);
    if (now < start) return 'PENDING';
    if (now > end) return 'EXPIRED';
    return 'ACTIVE';
};

const InstallerAccessesScreen: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [criteria, setCriteria] = useState<InstallerAccessGrantsSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderBy: 'desc',
        orderColumn: 'g.start_date'
    });
    const [page, setPage] = useState<Paged<InstallerAccessGrantDto>>();
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['INSTALLER_ACCESSES_WRITE']);
    const [filters, setFilters] = useState({ allIn: '' });
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<InstallerAccessGrantDto | null>(null);

    const getData = async () => {
        try {
            Loading.show();
            const result = await InstallerAccessesService.getList(criteria);
            setPage(result);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get installer accesses list', error);
            toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
        }
    };

    useEffect(() => {
        getData().catch(console.error);
    }, [criteria]);

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setCriteria({ ...criteria, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field });
    };

    const renderStatusBadge = (row: InstallerAccessGrantDto) => {
        const status = computeStatus(row);
        const variant = status === 'ACTIVE' ? 'success' : status === 'PENDING' ? 'warning' : 'secondary';
        return <Badge bg={variant}>{t(`installer_accesses.status.${status.toLowerCase()}`)}</Badge>;
    };

    const formatDate = (iso: string) => new Date(iso).toLocaleString();

    const renderActions = (row: InstallerAccessGrantDto) => (
        <Dropdown>
            <Dropdown.Toggle id="dropdown-basic-button" size="sm" variant="outline-secondary">...</Dropdown.Toggle>
            <Dropdown.Menu popperConfig={{ strategy: 'fixed' }} renderOnMount>
                <Dropdown.Item onClick={() => navigate(`/installer-access/details/${row.id}`)}>{t('common.details')}</Dropdown.Item>
                {hasWritePolicy && <>
                    <Dropdown.Item onClick={() => navigate(`/installer-access/edit/${row.id}`)}>{t('common.edit')}</Dropdown.Item>
                    <Dropdown.Item onClick={() => { setItemToRemove(row); setShowRemoveModal(true); }}>{t('common.delete')}</Dropdown.Item>
                </>}
            </Dropdown.Menu>
        </Dropdown>
    );

    const columns: ListingTableColumn<InstallerAccessGrantDto>[] = useMemo(() => [
        {
            field: 'installerCompanyName',
            name: t('installer_accesses.list.installer') ?? '',
            onSearch: onTableFilter,
            searchField: 'c.name'
        },
        {
            field: 'startDate',
            name: t('installer_accesses.list.start') ?? '',
            onSearch: onTableFilter,
            searchField: 'g.start_date',
            renderCell: (row: InstallerAccessGrantDto) => <>{formatDate(row.startDate)}</>
        },
        {
            field: 'endDate',
            name: t('installer_accesses.list.end') ?? '',
            onSearch: onTableFilter,
            searchField: 'g.end_date',
            renderCell: (row: InstallerAccessGrantDto) => <>{formatDate(row.endDate)}</>
        },
        {
            field: 'vehiclesCount',
            name: t('installer_accesses.list.vehicles_count') ?? '',
        },
        {
            name: t('installer_accesses.list.status') ?? '',
            renderCell: renderStatusBadge
        },
        {
            name: '',
            width: '60px',
            cellAlignment: 'right',
            preventClick: true,
            renderCell: renderActions,
            cellStyle: { overflow: 'unset' }
        },
    ], [t, hasWritePolicy]);

    const onRowClick = (event: any, id?: string) => {
        if (!id) return;
        if (event.ctrlKey) {
            window.open(location.pathname + '/details/' + id, '_blank');
            return;
        }
        navigate(`/installer-access/details/${id}`);
    };

    const onCancelRemove = () => { setItemToRemove(null); setShowRemoveModal(false); };

    const onRemove = async () => {
        if (!itemToRemove) {
            toast.error(t('messages.record_delete_error'));
            return;
        }
        try {
            await InstallerAccessesService.remove(itemToRemove);
            onCancelRemove();
            toast.success(t('messages.record_delete_success'));
            setCriteria({ ...criteria, page: 1 });
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete installer access', error);
            toast.error(t('messages.record_delete_error'));
        }
    };

    const handleChange = (e: any) => setFilters({ ...filters, [e.target.name]: e.target.value });
    const clearFilters = () => { setFilters({ allIn: '' }); setCriteria({ ...criteria, page: 1, allIn: '' }); };
    const onSearch = () => setCriteria({ ...criteria, allIn: filters.allIn });

    return (
        <ScreenTitle title={t('installer_accesses.title')}>
            <Breadcrumb>
                <Breadcrumb.Item active>{t('installer_accesses.title')}</Breadcrumb.Item>
            </Breadcrumb>
            <Container fluid>
                <Card className={styles.filters}>
                    <Card.Body>
                        <Stack direction="horizontal" gap={3}>
                            <Form.Control type="text" value={filters.allIn} className="me-auto" name="allIn" onChange={handleChange} onKeyDown={(e: any) => { if (e.key === 'Enter') onSearch(); }} placeholder={t('common.write_here')} />
                            <Button variant="secondary" onClick={onSearch}>{t('common.search')}</Button>
                            <div className="vr" />
                            <Button variant="outline-danger" onClick={clearFilters}>{t('common.clear')}</Button>
                            {hasWritePolicy && <Button variant="primary" onClick={() => navigate('/installer-access/new/')}>{t('common.new')}</Button>}
                        </Stack>
                    </Card.Body>
                </Card>
            </Container>
            <Container fluid>
                <ListingTable
                    columns={columns}
                    rows={page?.items || []}
                    onRowClick={(row, _, event) => onRowClick(event, row.id)}
                    allowHover={true}
                    initialSearch={{ colField: 'g.start_date', isOrderAsc: false }}
                />
                <PaginationWithInfo
                    itemName={t('installer_accesses.title')}
                    currentPage={page?.currentPage ?? 1}
                    pageItems={page?.items.length || 0}
                    totalItems={page?.totalItems || 0}
                    onChange={p => setCriteria({ ...criteria, page: p })}
                />
            </Container>
            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('messages.remove_record')} />
        </ScreenTitle>
    );
};

export default InstallerAccessesScreen;
